import { Chip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { t } from "i18next";
import { ChevronLeft, ChevronRight, GalleryHorizontal, Grid3x3, Pause, Play } from "lucide-react";
import { Swiper as SwiperType } from "swiper";
import { useWindowSize } from "../../hooks";
import { Navigation } from "../Carousel_v2/Carousel.styles";
import { LucidIcon } from "../LucidIcon/LucidIcon.component";
import { SweeperBullets } from "../Sweeper/SweeperBullet.component";

type IPaginationCarousel = {
  idScope: string;
  autoSweep: boolean;
  handleAutoSweep: () => void;
  swiperInstance: SwiperType | null;
  activeIndex: number;
  isBeginning?: boolean;
  isEnd?: boolean;
  onSetActiveIndex?: (index: number) => void;
  handleGridViewSwitch?: () => void;
  hasPagination?: boolean;
  hasPaginationSweeperBullets?: boolean;
  hasGreedView?: boolean;
  isCarouselView?: boolean;
  hideSweeperBulletsRight?: boolean;
  items: Record<string, unknown>[];
}


export const PaginationCarousel = ({
  idScope,
  autoSweep,
  handleAutoSweep,
  swiperInstance,
  activeIndex,
  onSetActiveIndex,
  handleGridViewSwitch,
  isCarouselView = true,
  hasPagination = false,
  hasPaginationSweeperBullets = false,
  hasGreedView = false,
  hideSweeperBulletsRight = false,
  items
}: IPaginationCarousel) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const windowSize = useWindowSize();
  // actions or infos after arrow buttons
  const LeftActions = () => {
    if (hasPagination) {
      return <Chip sx={ {
        backgroundColor: theme.customColors.backgroundPrimaryControl,
        border: "0.06rem solid " + theme.customColors.borderPrimaryControl,
        color: theme.customColors.textPrimaryControl,
        fontSize: "0.75rem",
        height: "2.1875rem",  
        fontWeight: "500"
      } } label={ `${activeIndex + 1} ${t("of")} ${items.length}` } />;
    }

    if (hasPaginationSweeperBullets) {
      // calculate number of bullets based on number of slides per view
      const bullets = () => {
        if (swiperInstance?.originalParams?.slidesPerView) {
          let bullets = items.length - Number(swiperInstance?.originalParams.slidesPerView) + 1;
          let bulletsArray = [];
          for (let i = 0; i < bullets; i++) {
            bulletsArray.push({ title: "", slide: <></> });
          }
          return bulletsArray;
        }
        return [];
      };

      return (
        <SweeperBullets
          bullets={ bullets() }
          swiperInstance={ swiperInstance }
          activeIndex={ activeIndex }
          setActiveIndex={ () => onSetActiveIndex }
        />
      );
    }

    return <></>;
  };

  const isButtonLocked = (btn: "left" | "right") => {
    if(swiperInstance?.isBeginning && btn === "left") return true;
    if(swiperInstance?.isEnd && btn === "right") return true;
    if(swiperInstance?.isLocked && windowSize) return true;

    return false;
  };

  let titleCarouselOnariaLabel = idScope.replace("-carousel-explore", "");
  if(titleCarouselOnariaLabel === "bigcarousel"){
    titleCarouselOnariaLabel = t("main_carousel");
  }

  return (
    // se la vista delle card non è di tipo carosello, va modificato il layout di Navigation
    <Navigation isCarouselView={ isCarouselView }>
      { /* left navigation actions */ }
      { isCarouselView && (
        <div
          tabIndex={ -1 }
          //role="presentation"
          //aria-roledescription="header with buttons to navigate between carousel slides"
          className="left-navigation-container"
        >
          <LucidIcon
            //tabIndex={ isButtonLocked("left") ? -1: 0 }
            tabIndex={ -1 } 
            aria-hidden={ true }
            id={ idScope + "-left-arrow" }
            disable={ isButtonLocked("left") }
            icon={ <ChevronLeft /> }
            onClick={ () => 
            {
              if(isButtonLocked("left")) return;
              swiperInstance?.slidePrev();
            } }
            ariaLabel={ t("carousel_arrow_back") }
          />
          <LucidIcon
            //tabIndex={ isButtonLocked("right") ? -1: 0 }
            aria-hidden={ true }
            tabIndex={  -1 }
            id={ idScope + "-right-arrow" }
            disable={ isButtonLocked("right") }
            icon={ <ChevronRight /> }
            onClick={ () => {
              if(isButtonLocked("right")) return;
              swiperInstance?.slideNext();
            } }
            ariaLabel={ t("carousel_arrow_forword") }
          />
          <LeftActions />
        </div>
      ) }

      { /* right navigation actions */ }
      <div className="right-navigation-container">

        { !hasPaginationSweeperBullets && !hideSweeperBulletsRight && isCarouselView && (
          <>
            { !isMobile && (
              <SweeperBullets bullets={ items } swiperInstance={ swiperInstance } activeIndex={ activeIndex }
                setActiveIndex={ () => onSetActiveIndex } />
            ) }

            <LucidIcon
              id={ idScope + "-play-pause-button" }
              icon={ autoSweep ? <Pause /> : <Play /> }
              onClick={ () => handleAutoSweep() }
              aria-hidden={ true } 
              tabIndex={ -1 }
              //ariaLabel={ t("carousel_icon_pause_aria_label",{ name:titleCarouselOnariaLabel ,option:autoSweep ? "pause" : "play"  }) }
            />
          </>
        ) }
        { hasGreedView && handleGridViewSwitch && (
          <LucidIcon
            aria-hidden={ true }
            tabIndex={  -1 }
            id={ idScope + "-grid-gallery-switch" }
            icon={ isCarouselView ? <Grid3x3 /> : <GalleryHorizontal /> }
            onClick={ () => handleGridViewSwitch() }
            //ariaLabel={   t("carousel_icon_view_aria_label",{ name:titleCarouselOnariaLabel ,view:isCarouselView ? "grid" : "gallery" }) }
          />
        ) }
      </div>
    </Navigation>
  );
};
